import React, { FC, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Notification as NotificationTagDs, NotificationBody, NotificationHeader } from '@adeccoux/tag-ds';
import { PageWrapper } from '@components/app/PageWrapper/PageWrapper';
import { Loader } from '@components/shared/Loader/Loader';
import { OOPages } from '@features/oneOnboarding/interfaces';
import { DELETED_TYPE, DeletedCandidateView } from '@features/oneOnboarding/pages/DeletedCandidateView';
import CustomCandidateProfileSidebar from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/CustomCandidateProfileSidebar/CustomCandidateProfileSidebar';
import { useCandidateProfileData } from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/hooks';
import { PageStepContext } from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/PageStepContext';
import { PageStepProvider } from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/PageStepProvider';
import { useGetUserFlow } from '@hooks/apiHooks';
import { Tenant } from '@hooks/useComponentMapping';
import { useDecodedParams } from '@hooks/useDecodedParams';
import useTelemetry from '@hooks/useTelemetry';
import { OnboardingStatus } from '@models/onboarding.model';
import { EventType, TargetName, TelemetryActor } from '@models/telemetry.model';
import Typography from '@mui/material/Typography';
import { RootState } from '@store/rootReducer';

import { useT } from '../../../oneOnboarding/hooks/useT';
import { JumpCondition, OOPageModel } from '../../../oneOnboarding/models/FormModel';
import { checkJumpConditions, OOFlowWrapper } from '../../../oneOnboarding/wrappers/FlowWrapper';

import { CandidateProfileSelectedPage } from './CandidateProfileSelectedPage/CandidateProfileSelectedPage';
import { CandidateProfileSidebar } from './CandidateProfileSidebar/CandidateProfileSidebar';

import styles from './NewCandidateProfilePage.module.scss';

const CandidateProfilePageComponent: FC = () => {
  const { t } = useT('stepper', 'recruiter');
  const { page, step, setPageStepContext } = useContext(PageStepContext);
  const [isInvalidStep, setIsInvalidStep] = useState<boolean>(false);
  const { clientOrganizationId, userId, applicationId, configurationId } = useDecodedParams();

  const removeJumpConditionFalseSteps = (steps: any, stepName: any): any => {
    return steps?.map((step: any) => {
      if (step.children) {
        step.children = step.children.filter((child: any) => !stepName.includes(child.step));
      }
      return step;
    });
  };

  const handlePageJumpCondition = (jumpCondition: JumpCondition[]) => {
    let newSkipStepName;
    let newSkipPageName;
    for (const condition of jumpCondition) {
      const { jumpTo, conditions } = condition;
      const targetStep = jumpTo?.targetStep;
      const targetPage = jumpTo?.targetPage;

      if (checkJumpConditions(conditions)) {
        newSkipStepName = targetStep;
        newSkipPageName = targetPage;
        break;
      }
    }

    return { newSkipStepName, newSkipPageName };
  };

  const handleStepJumpCondition = (step: any, result: string[]) => {
    let skipStepName: string | undefined;
    let skipPageName: string | undefined;
    const pages = step?.pages;
    for (const page of pages) {
      if (skipStepName && skipStepName !== step.name) {
        result.push(page.name);
        continue;
      }
      if (skipStepName && skipStepName === step.name) {
        skipStepName = undefined;
      }
      if (skipPageName && skipPageName !== page.name) {
        result.push(page.name);
        continue;
      }

      if (skipPageName && skipPageName === page.name) {
        skipPageName = undefined;
      }
      if (!page.jumpCondition) continue;

      const handlePageResult = handlePageJumpCondition(page.jumpCondition);
      skipStepName = handlePageResult?.newSkipStepName;
      skipPageName = handlePageResult?.newSkipPageName;
    }

    return { result };
  };

  const stepsRemovedCouseOfJumpCondition = (steps: any): any => {
    let result: string[] = [];
    for (const step of steps) {
      const handleStepResult = handleStepJumpCondition(step, result);
      result = handleStepResult.result;
    }

    return result;
  };

  const { data, isLoading } = useGetUserFlow(clientOrganizationId, configurationId, userId, applicationId, false);
  let recruiterSidebar = isLoading ? [] : data?.selectedConfiguration?.recruiterSidebar;
  const {
    steps,
    allSteps,
    currentStep,
    flowWrapper,
    userFlow,
    fetchFlowLoading,
    currentPage,
  } = useCandidateProfileData();
  const tenantId = useSelector((state: RootState) => state.tenant).tenantId;
  const { selectedOnboarding } = useSelector((state: RootState) => state.dashboard);

  const { createTelemetryAuthenticated } = useTelemetry();

  const stepsToRemove = stepsRemovedCouseOfJumpCondition(allSteps);
  recruiterSidebar = removeJumpConditionFalseSteps(recruiterSidebar, stepsToRemove);

  useEffect(() => {
    createTelemetryAuthenticated({
      applicationId: applicationId,
      clientOrganizationId: clientOrganizationId,
      configurationId: configurationId,
      eventType: EventType.PAGE_LOADED,
      targetName: TargetName.RecruiterCandidateDetails,
      tenantId: tenantId,
      candidateId: userId,
      actor: TelemetryActor.RECRUITER,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (steps.length && flowWrapper) {
      if (currentStep && currentStep.name === 'WORKPRO_INDUCTION_VERIFICATION') {
        setPageStepContext({ step: currentStep, page: currentStep?.pages?.[0] });
      } else if (currentStep && step === undefined) {
        if (['DOCUMENT_UPLOAD', 'DOCUMENT_UPLOAD2', 'RECRUITER_DOCUMENT_UPLOAD'].includes(currentStep.name)) {
          const documentUploadStep = flowWrapper.getStep(currentStep.name);
          setPageStepContext({ step: Object.assign({}, step, documentUploadStep), page: undefined });
        } else if (currentStep.name === 'DOCUMENT_VALIDATION2') {
          setPageStepContext({ step: flowWrapper.getStep('DOCUMENT_UPLOAD2'), page: undefined });
        } else if (currentStep.name === 'RECRUITER_DOCUMENT_VALIDATION') {
          setPageStepContext({ step: flowWrapper.getStep('RECRUITER_DOCUMENT_UPLOAD'), page: undefined });
        } else {
          setPageStepContext({ step: currentStep, page: page ?? currentPage ?? currentStep.pages[0] });
        }
      }
      const checkIsValidStep = OOFlowWrapper.isStepValid(userFlow?.selectedConfiguration?.flow, userFlow?.currentStep);
      if (!checkIsValidStep) {
        setIsInvalidStep(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowWrapper, steps.length]);

  if (fetchFlowLoading || flowWrapper === undefined) {
    return <Loader />;
  }

  if (Tenant.Australia === tenantId && selectedOnboarding) {
    if (selectedOnboarding?.status === OnboardingStatus.aborted) {
      return <DeletedCandidateView deletedType={DELETED_TYPE.SOFT} />;
    }
    if (!selectedOnboarding?.userId) {
      return <DeletedCandidateView deletedType={DELETED_TYPE.HARD} />;
    }
  }

  return (
    <>
      {isInvalidStep && (
        <NotificationTagDs
          icon={<span className="material-icons">info_outline</span>}
          iconClose
          onClose={() => setIsInvalidStep(false)}
          error
          show
          className={`tag-ds ${styles.notification}`}
        >
          <NotificationHeader>{t('recruiter:GENERAL.CANDIDATES_OVERVIEW.error')}</NotificationHeader>
          <NotificationBody>
            <Typography>{t('recruiter:GENERAL.CANDIDATES_OVERVIEW.errorInvalidStep')}</Typography>
          </NotificationBody>
        </NotificationTagDs>
      )}
      <PageWrapper
        featureName="candidate-profile"
        pageName="new-candidates-profile"
        sideMenu
        headerConfig={{ pageName: t('recruiter:GENERAL.CANDIDATE_DETAILS.Candidate details'), languagePicker: true }}
      >
        <div className={`${styles.candidateProfilePage} admin`}>
          {!!flowWrapper || isLoading ? (
            <>
              {recruiterSidebar && recruiterSidebar.length > 0 ? (
                <CustomCandidateProfileSidebar
                  items={recruiterSidebar}
                  flowWrapper={flowWrapper}
                  steps={allSteps}
                  currentStep={currentStep?.name || 'CANDIDATE_DETAILS'}
                  currentPage={currentPage?.name as OOPages}
                />
              ) : (
                <CandidateProfileSidebar
                  onSelect={(step, page) => {
                    setPageStepContext({ step, page });
                  }}
                  flowWrapper={flowWrapper}
                  steps={steps}
                  allSteps={allSteps}
                  currentStep={currentStep?.name || 'CANDIDATE_DETAILS'}
                />
              )}
            </>
          ) : (
            <Loader />
          )}

          <div className={styles.content}>
            <CandidateProfileSelectedPage
              page={page}
              step={step}
              flowWrapper={flowWrapper}
              setPage={(page: OOPageModel) => setPageStepContext({ page, step })}
            />
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export const CandidateProfilePage: FC = () => (
  <PageStepProvider>
    <CandidateProfilePageComponent />
  </PageStepProvider>
);
